<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import type { Rendering } from '~/types/api'

const { t, te } = useI18n()
const { trimDescription } = useMeta()

useSeoMeta({
  title: t('pages.lookbook.meta.title'),
  description: te('pages.lookbook.meta.description')
    ? t('pages.lookbook.meta.description')
    : trimDescription(t('pages.lookbook.description')),
})

const renderings = ref<Rendering[]>([])
const page = ref(1)
const itemsPerPage = ref(10)
const pageCount = ref(1)
const itemCount = ref(1)

const {
  data: renderingsData,
  status,
  refresh,
} = await useLazyAsyncData(() =>
  createRenderingsRepository().getAll({
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    sort: APIFilters.makeSort({ weight: 'ASC', nid: 'DESC' }),
  })
)

async function fetchData() {
  if (status.value === 'pending') {
    return
  }

  await refresh()

  page.value++

  pageCount.value = renderingsData.value?.pageCount ?? 1
  itemCount.value = renderings.value.length
}

watch(
  renderingsData,
  () => {
    renderings.value.push(...(renderingsData.value?.items ?? []))
  },
  { immediate: true }
)

const observer = ref(null)
const endOfPage = ref(null)
function handleIntersect(observedEntries: IntersectionObserverEntry[]) {
  if (observedEntries.length === 0 || !observedEntries[0]?.isIntersecting) {
    return
  }
  if (page.value === pageCount.value) {
    return
  }

  fetchData()
}

useIntersectionObserver(observer, handleIntersect)
useIntersectionObserver(endOfPage, handleIntersect)
</script>

<template>
  <main>
    <UiBanner
      :title="t('pages.lookbook.label')"
      :description="t('pages.lookbook.description')"
    />
    <section>
      <div class="container">
        <div class="mb-6">
          <div class="flex flex-wrap">
            <template
              v-for="(rendering, index) in renderings"
              :key="rendering.nid"
            >
              <div
                v-if="index === itemCount - itemsPerPage / 2"
                ref="observer"
              />
              <LookbookCard
                class="mb-4 px-2"
                :class="{
                  'md:w-1/2': index % 9 === 1 || index % 9 === 2,
                  'md:w-5/12': index % 9 === 5 || index % 9 === 7,
                  'md:w-7/12': index % 9 === 4 || index % 9 === 8,
                }"
                :rendering="rendering"
              />
            </template>
            <div ref="endOfPage" />
          </div>
        </div>
      </div>
    </section>
    <NewsSubscribe class="bg-neutral-50" />
  </main>
</template>
